import { startTransition, StrictMode } from 'react';
import { hydrateRoot } from 'react-dom/client';
import { HydratedRouter } from 'react-router/dom';
import { reportError } from '@/lib/telemetry';
import { installVitePreloadErrorRecovery } from '@/lib/runtime/vitePreloadErrorRecovery';
import { isNewsletterMarketingPathname } from '@/routes';

installVitePreloadErrorRecovery({
  target: window,
  storage: {
    getItem: (key) => window.sessionStorage.getItem(key),
    setItem: (key, value) => window.sessionStorage.setItem(key, value),
  },
  buildId: import.meta.env.VITE_APP_BUILD_ID ?? 'unknown',
  reload: () => window.location.reload(),
  report: reportError,
});

// The current application page needs this runtime. Start fetching it before
// hydration reaches RouteExperience; both imports share the browser module cache.
if (!isNewsletterMarketingPathname(window.location.pathname)) {
  // Player routes need auth immediately. Marketing session discovery owns its
  // saved-session check, so fresh visitors need not construct the backend SDK.
  void import('@/lib/supabase').catch(() => {
    // Vite owns preload recovery; SessionManager retains unresolved auth and retry UI.
  });
  void import('./layouts/ApplicationRuntime').catch(() => {
    // Vite's recovery listener owns reloads; React.lazy retains render errors.
  });
}

// Error handler for HydratedRouter
const handleError = (error: unknown, errorInfo?: unknown) => {
  console.error('❌ [entry.client.tsx] Router error:', error, errorInfo);
};

startTransition(() => {
  try {
    hydrateRoot(
      document,
      <StrictMode>
        <HydratedRouter onError={handleError} />
      </StrictMode>,
      {
        onRecoverableError: (error, errorInfo) => {
          reportError({
            error,
            source: 'ReactHydration:onRecoverableError',
            severity: 'warning',
            react: { componentStack: errorInfo.componentStack ?? undefined },
          });
        },
      }
    );
  } catch (error) {
    console.error('❌ [entry.client.tsx] Hydration error:', error);
    throw error;
  }
});
